import { Extension } from "@tiptap/core";
import { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import HardBreak from "@tiptap/extension-hard-break";
import {
  Table,
  TableRow,
  TableHeader,
  TableCell,
} from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import { CellSelection, selectedRect, isInTable } from "@tiptap/pm/tables";
import { MustacheText } from "./mustacheTags";

const CustomHardBreak = HardBreak.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any, parent: any, index: number) {
          for (let i = index + 1; i < parent.childCount; i++)
            if (parent.child(i).type !== node.type) {
              state.write(state.inTable ? "<br>" : "\n");
              return;
            }
        },
        parse: {},
      },
    };
  },
});

// Legacy override: makes a single Enter a hard break (line break) rather than a
// paragraph split, so it takes two Enters — or Shift+Enter — to start a new
// paragraph. This matches the pre-conformant decorator behavior. Gated OFF for
// accounts on `release.common-marker` so the editor uses default markdown
// behavior (Enter = paragraph, Shift+Enter = hard break).
const SingleEnterBreak = Extension.create({
  name: "singleEnterBreak",
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        if (
          editor.isActive("listItem") ||
          editor.isActive("tableCell") ||
          editor.isActive("tableHeader")
        ) {
          return false;
        }

        const { $from } = editor.state.selection;
        if ($from.nodeBefore?.type.name === "hardBreak") {
          return editor
            .chain()
            .deleteRange({ from: $from.pos - 1, to: $from.pos })
            .splitBlock()
            .run();
        }

        return editor.chain().setHardBreak().run();
      },
      "Shift-Enter": ({ editor }) => {
        return editor.chain().createParagraphNear().run();
      },
    };
  },
});

// When a table cell selection covers whole rows, columns, or the entire
// table, Backspace/Delete removes those rows/columns/the table instead of
// just clearing cell contents (ProseMirror's default for a CellSelection).
// Partial selections fall through to the default content-clearing behavior.
const TableSelectionDelete = Extension.create({
  name: "tableSelectionDelete",
  addKeyboardShortcuts() {
    const handleDelete = ({ editor }: { editor: Editor }) => {
      const { state } = editor;
      if (!(state.selection instanceof CellSelection) || !isInTable(state)) {
        return false;
      }

      const rect = selectedRect(state);
      const spansAllColumns = rect.left === 0 && rect.right === rect.map.width;
      const spansAllRows = rect.top === 0 && rect.bottom === rect.map.height;

      if (spansAllColumns && spansAllRows) {
        return editor.chain().focus().deleteTable().run();
      }
      if (spansAllColumns) {
        return editor.chain().focus().deleteRow().run();
      }
      if (spansAllRows) {
        return editor.chain().focus().deleteColumn().run();
      }
      return false;
    };

    return { Backspace: handleDelete, Delete: handleDelete };
  },
});

interface BuildExtensionsOptions {
  // When true (account on `release.common-marker`), use default markdown break
  // behavior. When false, apply the legacy SingleEnterBreak override.
  conformant: boolean;
  placeholder?: string;
}

export const buildEditorExtensions = ({
  conformant,
  placeholder = "",
}: BuildExtensionsOptions) => [
  StarterKit.configure({
    heading: { levels: [1, 2, 3, 4] },
    link: { openOnClick: false, autolink: false },
    hardBreak: false,
    text: false,
  }),
  MustacheText,
  CustomHardBreak,
  ...(conformant ? [] : [SingleEnterBreak]),
  TableSelectionDelete,
  Table.configure({ resizable: true }),
  TableRow,
  TableHeader,
  TableCell,
  Placeholder.configure({ placeholder }),
  Markdown.configure({
    html: false,
    breaks: true,
    transformPastedText: true,
    transformCopiedText: true,
  }),
];
